import * as React from 'react';
import { t, Trans } from '@lingui/macro';
import JobOfferItem from './jobOfferItem';
import JobOfferLoader from './jobOfferLoader';
import { getSectorOfActivityLabel, SectorOfActivities, sectorOfActivitiesLabels } from '../../helpers/sectorOfActivities';
import JobFunction, { getJobFunctionLabel } from '../../models/jobFunction';
import { i18n } from '@lingui/core';
import LocationInput from '../../shared/locationInput';

class Values {
  sectorOfActivityFilter = '';
  jobFunctionFilter = null;
  locationFilter = null;
  provinceLocationId = null;
}

type LocationDto = {
  id: number;
  name: string;
}

interface JobOffersState {
  jobOffers: any[];
  jobFunctions: JobFunction[];
  locations: LocationDto[];
  employeeLocationProvinceId: number;
  employeeLocationProvinceName: string;
  values: Values;
  animated: boolean;
  isLoading: boolean;
  isAnonymous: boolean;
}

interface JobOffersProps {
}


export default class JobOffersEmployee extends React.Component<JobOffersProps, JobOffersState> {
  constructor(props: JobOffersProps) {
    super(props);

    this.state = {
      jobOffers: [],
      jobFunctions: [],
      locations: [],
      employeeLocationProvinceId: null,
      employeeLocationProvinceName: null,
      values: new Values(),
      animated: false,
      isLoading: true,
      isAnonymous: true
    };
  };

  componentDidMount = async () => {
    await this.loadProfile();
    await this.loadLevel2Locations();
    await this.loadData();
    await this.loadJobFunctions();
  };

  loadProfile = async () => {
    let response = await fetch('/api/profile/me', {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });

    const user = await response.json();
    this.setState({ ...this.state, isAnonymous: user.isAnonymous });
  };

  onChangeInputHandler = (field, value) => {
    this.setState((prevState) => ({
      values: { ...prevState.values, [field]: value }
    }));
  };

  loadData = async () => {
    let response = await fetch('/api/job-offers', {
      method: 'GET',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      credentials: 'include'
    });

    if (response.ok) {
      let data = await response.json();
      this.setState({
        jobOffers: data.jobOffers,
        isLoading: false,
        employeeLocationProvinceId: data.employeeLocationProvinceId,
        employeeLocationProvinceName: data.employeeLocationProvinceName
      });
    } else {
      this.setState({ isLoading: false });
    }
  };

  loadJobFunctions = async () => {
    let response = await fetch('/api/job-function', {
      method: 'GET',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      credentials: 'include'
    });

    if (response.ok) {
      let jobFunctions = await response.json();
      this.setState({ jobFunctions });
    }
  };

  loadLevel2Locations = async () => {
    let response = await fetch('/api/location/level/2', {
      method: 'GET',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      credentials: 'include'
    });

    if (response.ok) {
      let data = await response.json();
      this.setState({ locations: data });
    }
  }

  getFilteredJobOffers() {
    const { jobOffers, values } = this.state;
    const { sectorOfActivityFilter, jobFunctionFilter, locationFilter, provinceLocationId } = values;

    return jobOffers.filter((e) => {
      const companyProfile = e.companyProfile;

      const jobFunctionId = e.jobFunction?.id || companyProfile?.jobFunction.id;
      const sectorOfActivityId = companyProfile.sectorOfActivity;
      const companyProvinceLocationId = companyProfile.companyProvinceLocationId;
      const companyAddressLocationId = companyProfile.companyAddressLocation?.id;

      const matchesSector = sectorOfActivityFilter
        ? sectorOfActivityId === parseInt(sectorOfActivityFilter) ||
        (sectorOfActivityId === SectorOfActivities.InsuranceAndBank &&
          [SectorOfActivities.Insurance, SectorOfActivities.Bank].includes(parseInt(sectorOfActivityFilter)))
        : true;

      const matchesJobFunction = jobFunctionFilter
        ? jobFunctionId === parseInt(jobFunctionFilter)
        : true;

      const matchesProvince = provinceLocationId
        ? e.jobOfferProvinceLocationId === provinceLocationId || companyProvinceLocationId === provinceLocationId
        : true;

      const matchesLocation = locationFilter
        ? e.jobOfferLocation.id === locationFilter || companyAddressLocationId === locationFilter
        : true;

      return matchesSector && matchesJobFunction && matchesProvince && matchesLocation;
    });
  }

  getLocationLabel = (id) => {
    const location = this.state.locations.find((l) => l.id === id);
    return location ? location.name : t`Ailleurs`;
  }

  renderFilters = () => {
    const { values, jobFunctions } = this.state;
    return (
      <div className="form-block blue text-white mb-4">
        <div className={"row"}>
          <div className={"col-6 col-lg-3"}>
            <select
              className="field w-input"
              value={values.sectorOfActivityFilter || ''}
              onChange={(e) => this.onChangeInputHandler('sectorOfActivityFilter', e.target.value)}
            >
              <option value="">{t`Secteur d'activité`}</option>
              {sectorOfActivitiesLabels.map((sector, index) => (
                <option key={index} value={sector.value}>{getSectorOfActivityLabel(sector.value)}</option>
              ))}
            </select>
          </div>
          <div className={"col-6 col-lg-3"}>
            <select
              className="field w-input"
              value={values.jobFunctionFilter || ''}
              onChange={(e) => this.onChangeInputHandler('jobFunctionFilter', e.target.value)}
            >
              <option value="">{t`Fonction`}</option>
              {jobFunctions.map((jobFunction) => (
                <option key={jobFunction.id} value={jobFunction.id}>
                  {getJobFunctionLabel(jobFunction, i18n.locale)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <LocationInput
          locationId={values.locationFilter}
          onChangeLocationId={(id) => this.onChangeInputHandler('locationFilter', id)}
          provinceFilter={true}
          onChangeProvinceLocationId={(id) => this.onChangeInputHandler('provinceLocationId', id)}
        />
      </div>
    );
  };

  renderJobOffers = (title, offers) => {
    return offers.length > 0 ? (
      <div>
        <h2>{title}</h2>
        <div className="flex wrap">
          {offers.map((offer, index) => (
            <JobOfferItem key={index} jobOffer={offer} id={offer.id} isAnonymous={this.state.isAnonymous} />
          ))}
        </div>
      </div>
    ) : null;
  };

  render() {
    const { isLoading } = this.state;
    const filteredOffers = this.getFilteredJobOffers();

    const provinceOffers = filteredOffers.filter((e) => {
      if (e.jobOfferProvinceLocationId) {
        return e.jobOfferProvinceLocationId === this.state.employeeLocationProvinceId;
      }

      if (e.companyProfile.companyProvinceLocationId) {
        return e.companyProfile.companyProvinceLocationId === this.state.employeeLocationProvinceId;
      }

      return false;
    });

    const otherOffers = this.state.values.provinceLocationId !== this.state.employeeLocationProvinceId ?
      filteredOffers.filter((e) => {
        const offerProvince = e.jobOfferProvinceLocationId;
        const companyProvince = e.companyProfile.companyProvinceLocationId;
        const employeeProvince = this.state.employeeLocationProvinceId;

        return (
          (offerProvince !== null && offerProvince !== employeeProvince) ||
          (offerProvince === null && companyProvince !== null && companyProvince !== employeeProvince) ||
          (offerProvince === null && companyProvince === null)
        );
      }) : [];

    const groupedOffers = otherOffers.reduce((acc, offer) => {
      const key = offer.jobOfferProvinceLocationId || offer.companyProfile.companyProvinceLocationId || 'other';
      acc[key] = acc[key] || [];
      acc[key].push(offer);
      return acc;
    }, {});


    const sortedJobOffers = Object.keys(groupedOffers).map((key) => {
      return {
        key: key,
        label: key === 'other' ? t`Ailleurs` : this.getLocationLabel(parseInt(key)),
        offers: groupedOffers[key]
      }
    }).sort((a, b) => a.label.localeCompare(b.label));

    return (
      <div className="fadeIn animated fast">
        <div className="container-heading">
          <h1 className="bolder">
            <Trans>Rechercher une offre d'emploi</Trans>
          </h1>
        </div>
        {this.renderFilters()}
        {isLoading ? (
          <div className="flex wrap">
            {Array.from({ length: 4 }).map((_, i) => <JobOfferLoader key={i} />)}
          </div>
        ) : (
          <>
            {this.renderJobOffers(this.getLocationLabel(this.state.employeeLocationProvinceId), provinceOffers)}
            {sortedJobOffers.map((group) => {
              return this.renderJobOffers(group.label, group.offers);
            })}
            {provinceOffers.length === 0 && otherOffers.length === 0 && (
              <div>
                <p>
                  <Trans>
                    Aucune offre d'emploi ne correspond à vos critères de recherche.</Trans>
                </p>
              </div>
            )}
          </>
        )}
      </div>
    );
  }
}